import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { schema } from '../__generated__/schema-lite';
import PendingApprovalPage from '../pages/PendingApprovalPage';
import { AppLoader } from '../components/AppLoader';
import { useAuth } from './AuthContext';

export function ProtectedRoute() {
  const { token, setToken } = useAuth();
  const location = useLocation();
  const crud = useClientQueries(schema);
  // `retry: false` — /me is an identity check, not data. If it fails (expired,
  // or the account was deleted/disabled after login), don't retry for seconds;
  // treat it as logged-out immediately.
  const { data: user, isLoading, isError } = crud.$procs.me.useQuery(undefined, {
    enabled: !!token,
    retry: false,
  });

  // Identity check hung: if `/me` never settles (cold-started / unreachable API
  // that holds the connection without answering), we'd never get an error or a
  // 401 — so neither the effect below nor the authFetch 401 interceptor fires,
  // and the loader spins forever. After a few seconds of a still-pending check,
  // treat the session as dead: drop the token and fall through to /login.
  const [identityStuck, setIdentityStuck] = useState(false);
  useEffect(() => {
    if (!token || !isLoading) {
      setIdentityStuck(false);
      return;
    }
    const id = window.setTimeout(() => setIdentityStuck(true), 8000);
    return () => window.clearTimeout(id);
  }, [token, isLoading]);

  // A token that no longer resolves to a user (deleted account, rotated secret,
  // expiry) — or one whose check hung past the timeout — must be dropped,
  // otherwise `!user` below would spin the loader forever. Clear it in an effect
  // (never set state during render).
  useEffect(() => {
    if (token && (isError || identityStuck)) setToken(null);
  }, [token, isError, identityStuck, setToken]);

  if (!token) return <Navigate to="/login" replace state={{ from: location }} />;
  if (isError || identityStuck) return <Navigate to="/login" replace state={{ from: location }} />;
  // Wait for /me before deciding — otherwise an approved user briefly sees the
  // pending page on refresh. Show a loader (not a blank frame) so the refresh
  // doesn't flash empty content before the page mounts.
  if (isLoading || !user) return <AppLoader />;
  if ((user as { approved?: boolean }).approved !== true) return <PendingApprovalPage />;
  return <Outlet />;
}
